'use client';

import React from 'react';
import { useLanguage } from '@/context/LanguageContext';

type Section = { h: string; p: string[] };
type Doc = { title: string; updated: string; intro: string; sections: Section[] };

const CONTENT: Record<'es' | 'en', Doc> = {
  es: {
    title: 'Política de Privacidad',
    updated: 'Última actualización: 15 de junio de 2026',
    intro:
      'MERCALIA Connect Group LLC ("MERCALIA", "nosotros") respeta su privacidad. Esta política explica qué datos personales recopilamos a través de mercaliacg.com, cómo los usamos y qué derechos tiene usted sobre ellos.',
    sections: [
      {
        h: '1. Datos que recopilamos',
        p: [
          'Datos que usted nos facilita: nombre, empresa, correo electrónico, teléfono y el contenido de los mensajes que envía a través de nuestros formularios de contacto o por correo.',
          'Datos técnicos recopilados automáticamente: dirección IP, tipo de navegador, páginas visitadas y datos de uso, mediante cookies o tecnologías similares.',
        ],
      },
      {
        h: '2. Cómo usamos sus datos',
        p: [
          'Para responder a sus consultas y gestionar relaciones comerciales de abastecimiento y comercio de materias primas.',
          'Para cumplir obligaciones legales y de cumplimiento (incluidas verificaciones KYC/AML cuando correspondan).',
          'Para mejorar nuestro sitio web y nuestros servicios.',
        ],
      },
      {
        h: '3. Base legal y conservación',
        p: [
          'Tratamos sus datos sobre la base de su consentimiento, la ejecución de un contrato o nuestro interés legítimo en operar el negocio. Conservamos los datos solo durante el tiempo necesario para esos fines o según exija la ley.',
        ],
      },
      {
        h: '4. Compartición de datos',
        p: [
          'No vendemos sus datos personales. Podemos compartirlos con proveedores de servicios que actúan en nuestro nombre (por ejemplo, alojamiento o correo) y con autoridades cuando la ley lo exija.',
        ],
      },
      {
        h: '5. Sus derechos (GDPR / CCPA)',
        p: [
          'Según su jurisdicción, usted puede tener derecho a acceder, corregir, eliminar o portar sus datos, así como a oponerse o limitar su tratamiento. Para ejercerlos, escríbanos a compliance@mercaliacg.com.',
        ],
      },
      {
        h: '6. Cookies',
        p: [
          'Usamos cookies esenciales para el funcionamiento del sitio y, en su caso, cookies analíticas. Puede gestionarlas desde la configuración de su navegador.',
        ],
      },
      {
        h: '7. Contacto',
        p: [
          'MERCALIA Connect Group LLC — Nuevo México, EE. UU. Para cualquier asunto de privacidad: compliance@mercaliacg.com.',
        ],
      },
    ],
  },
  en: {
    title: 'Privacy Policy',
    updated: 'Last updated: June 15, 2026',
    intro:
      'MERCALIA Connect Group LLC ("MERCALIA", "we") respects your privacy. This policy explains what personal data we collect through mercaliacg.com, how we use it, and the rights you have over it.',
    sections: [
      {
        h: '1. Data we collect',
        p: [
          'Data you provide: name, company, email, phone, and the content of messages you send through our contact forms or by email.',
          'Technical data collected automatically: IP address, browser type, pages visited, and usage data, via cookies or similar technologies.',
        ],
      },
      {
        h: '2. How we use your data',
        p: [
          'To respond to your inquiries and manage commodity sourcing and trading relationships.',
          'To meet legal and compliance obligations (including KYC/AML checks where applicable).',
          'To improve our website and services.',
        ],
      },
      {
        h: '3. Legal basis and retention',
        p: [
          'We process your data based on your consent, the performance of a contract, or our legitimate interest in operating the business. We keep data only as long as necessary for those purposes or as required by law.',
        ],
      },
      {
        h: '4. Data sharing',
        p: [
          'We do not sell your personal data. We may share it with service providers acting on our behalf (e.g., hosting or email) and with authorities where required by law.',
        ],
      },
      {
        h: '5. Your rights (GDPR / CCPA)',
        p: [
          'Depending on your jurisdiction, you may have the right to access, correct, delete, or port your data, and to object to or restrict its processing. To exercise these, contact compliance@mercaliacg.com.',
        ],
      },
      {
        h: '6. Cookies',
        p: [
          'We use essential cookies for the site to function and, where applicable, analytics cookies. You can manage them through your browser settings.',
        ],
      },
      {
        h: '7. Contact',
        p: [
          'MERCALIA Connect Group LLC — New Mexico, USA. For any privacy matter: compliance@mercaliacg.com.',
        ],
      },
    ],
  },
};

export default function PrivacyContent() {
  const { lang } = useLanguage();
  const doc = CONTENT[lang] ?? CONTENT.en;

  return (
    <section className="relative pt-36 pb-24 px-6">
      <div className="max-w-3xl mx-auto">
        <h1
          className="font-display font-bold text-primary mb-3"
          style={{ fontSize: 'clamp(36px, 5vw, 56px)', letterSpacing: '-0.03em', lineHeight: 1.05 }}
        >
          {doc.title}
        </h1>
        <p className="text-sm text-brand-muted mb-10">{doc.updated}</p>
        <p className="text-brand-muted leading-relaxed mb-10">{doc.intro}</p>

        <div className="flex flex-col gap-8">
          {doc.sections.map((s) => (
            <div key={s.h}>
              <h2 className="font-display text-xl font-bold text-primary mb-3">{s.h}</h2>
              {s.p.map((para, i) => (
                <p key={i} className="text-brand-muted leading-relaxed mb-2">
                  {para}
                </p>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
